import React, { useState } from "react";
import { Link } from "react-router-dom";
import Meta from "../components/Meta";
import hero1 from "../imgs/hero 1.png";
import hero2 from "../imgs/hero 2.jpg";
import hero3 from "../imgs/hero 3.png";
import hero4 from "../imgs/hero 4.jpg";
import hero5 from "../imgs/hero 5.jpg";

/* ── Reusable CTA button ── */
const CtaButton = ({ text = "Generate Free Nudes →", large = false }) => (
  <Link
    to="/create"
    className={`group relative inline-flex items-center gap-2 whitespace-nowrap rounded-xl font-black text-white transition hover:scale-105 active:scale-110 ${large ? "px-10 py-4 text-lg" : "px-7 py-3 text-base"}`}
    style={{ background: "linear-gradient(135deg,#7b00d4,#ec008c,#fc6767)" }}
  >
    <span className="absolute inset-0 rounded-xl opacity-0 transition group-hover:opacity-15" style={{ background: "white" }} />
    {text}
  </Link>
);

/* ── Accordion FAQ ── */
const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 cursor-pointer" onClick={() => setOpen(o => !o)}>
      <div className="flex items-center justify-between py-4 px-1 gap-4">
        <span className="text-sm font-semibold leading-snug">{q}</span>
        <span className="flex-shrink-0 text-xl font-black transition-transform duration-200 text-purple-400"
          style={{ transform: open ? "rotate(45deg)" : "rotate(0deg)" }}>+</span>
      </div>
      {open && <p className="px-1 pb-4 text-sm text-foreground/60 leading-relaxed">{a}</p>}
    </div>
  );
};

/* ── Feature rows data ── */
const features = [
  {
    heading: "Generate Unlimited Nude AI Art From Any Prompt",
    body: "Stop spending hours searching for nude images that never match what you actually want. Describe your ideal scene — body type, skin tone, pose, lighting, setting — and our AI Nude Generator renders it from scratch in under 10 seconds. No technical skill required. No limits on how many times you generate.",
    image: hero1,
    alt: "AI nude generator creating custom nude art from prompt",
    badge: "01",
  },
  {
    heading: "Realistic or Anime — Your Style, Your Rules",
    body: "Choose between photorealistic nude images that look like professional photography, or anime/hentai-style art with stylized proportions and vibrant aesthetics. The same prompt produces radically different results depending on the style you pick. Switch styles freely on every generation — no restrictions.",
    image: hero2,
    alt: "Realistic and anime style AI generated nude art",
    badge: "02",
  },
  {
    heading: "Explore an Endless Gallery of AI Nude Art",
    body: "Beyond generating your own images, browse a community gallery filled with AI nude art across every style and aesthetic. From hyper-realistic portraits to fantasy and sci-fi nudes, the gallery showcases what's possible with a well-crafted prompt — and gives you inspiration to refine your own creations.",
    image: hero3,
    alt: "Gallery of AI nude art in various styles",
    badge: "03",
  },
  {
    heading: "100% Private. Nothing Stored Without Permission.",
    body: "Your prompts and generated images never leave your session without your consent. We don't log generation history, share data with third parties, or display your content to other users. What you create stays private. Full stop. An HTTPS-encrypted connection protects every request from your browser to our servers.",
    image: hero4,
    alt: "Private and secure AI nude generator",
    badge: "04",
  },
];

/* ── Steps data ── */
const steps = [
  { n: "1", title: "Open the Generator", desc: 'Click any "Generate" button on this page to open the AI Nude Generator directly — no homepage detour.' },
  { n: "2", title: "Sign Up Free", desc: "Create a free account with just your email. No credit card. Takes 30 seconds. You get free credits immediately." },
  { n: "3", title: "Write Your Prompt", desc: 'Describe the nude image you want — body type, skin tone, pose, setting, style. Be specific for best results. Example: "topless woman, natural breasts, soft bedroom lighting, realistic, 8K".' },
  { n: "4", title: "Pick a Style", desc: "Choose Realistic, Anime, 3D CGI, or Fantasy from the style selector. Each produces a visually distinct result from the same prompt." },
  { n: "5", title: "Generate & Download", desc: "Hit Generate. Your nude AI image is ready in 8–12 seconds. Download it watermark-free, full resolution, instantly." },
];

/* ── Styles data ── */
const styles = [
  { emoji: "📷", title: "Hyper-Realistic", desc: "Photo-quality nudes with pore-level skin detail, natural body proportions, accurate lighting, and depth of field. Looks like a real photograph." },
  { emoji: "🌸", title: "Anime / Hentai", desc: "Stylized nude art with exaggerated proportions, smooth gradient shading, vibrant colors, and the iconic visual language of hentai and ecchi genres." },
  { emoji: "💠", title: "3D CGI", desc: "Rendered nude figures with glossy skin, cinematic studio lighting, subsurface scattering, and sculpted perfection — like a high-end 3D art render." },
  { emoji: "🧝", title: "Fantasy", desc: "Nude elves, sorceresses, demons — sensual characters in magical worlds with dramatic lighting, ornate details, and creative body aesthetics." },
  { emoji: "🖌️", title: "Painterly", desc: "Soft brushstroke nude art with warm tones and an artistic, gallery-quality feel. Resembles a high-quality NSFW commission from a professional artist." },
  { emoji: "🎞️", title: "Pin-Up / Retro", desc: "Classic curves in 1950s–60s style — bold colors, warm film tones, playful poses, and the confident energy of vintage adult illustration." },
];

/* ── FAQ data ── */
const faqs = [
  { q: "Is the AI Nude Generator free?", a: "Yes. Free generation credits are given when you sign up — no credit card required. The free tier is enough to create several high-quality nude images across multiple styles before deciding whether to upgrade." },
  { q: "Are images generated without watermarks?", a: "Yes. All generated images — on both free and paid plans — are delivered without watermarks. Download your nude AI art clean and full-resolution, ready to save or share privately." },
  { q: "Is my data and generation history private?", a: "Completely. Your prompts, generated images, and account information are never shared, sold, or displayed to other users. Your generation history is private to your account and can be deleted at any time." },
  { q: "Can I generate fully nude images?", a: "Yes. Unlike mainstream AI tools that refuse NSFW prompts, this platform is built specifically for explicit adult content. Fully nude images — topless, fully nude, explicit poses — are all supported without censorship." },
  { q: "What styles are available?", a: "Realistic, Anime/Hentai, 3D CGI, Fantasy, Painterly, and Retro/Pin-Up. Each style produces a visually distinct output — not just a filter, but a different rendering model entirely." },
  { q: "Can I specify body type, skin tone, and features?", a: "Yes. Describe everything in your prompt: skin tone, breast size, body type (petite, curvy, athletic), hair color, eye color, age range, and any specific physical features. The AI renders exactly what you describe." },
  { q: "How realistic are the nude images?", a: "Very. The model is fine-tuned for adult content realism and handles skin texture, natural body proportions, realistic lighting, and detailed anatomy accurately. Adding 'RAW photo, 8K, Sony A7R' to prompts pushes realism further." },
  { q: "Is there a daily generation limit?", a: "Free accounts have a daily generation cap that resets every 24 hours. Premium subscribers generate unlimited nude images per day with no restrictions on style, content, or resolution." },
  { q: "Can I save my generated images?", a: "Yes. Log into your account and save any generated image to your private gallery. Saved images are accessible only to you and can be deleted at any time from your account dashboard." },
  { q: "Do I need any skills to use this?", a: "None at all. You just type what you want in plain English. The better you describe the image, the better the result — but even simple prompts like 'nude woman, beach, realistic' produce high-quality outputs." },
];

/* ── Page component ── */
const AiNudePage = () => {
  return (
    <>
      <Meta
        title="AI Nude Generator – Free Realistic & Anime Nude AI Art"
        description="Generate stunning nude AI images in seconds. Realistic, anime, 3D, fantasy — describe what you want and the AI creates it. Free to start, no watermarks, 100% private."
        meta={{ robots: "index, follow" }}
      />

      <main className="flex-auto bg-background-body">

        {/* ── HERO ── */}
        <section className="relative min-h-[90vh] flex flex-col items-center justify-center text-center px-4 overflow-hidden"
          style={{ background: "radial-gradient(ellipse at 50% 30%, #1a0030 0%, #0a000f 70%)" }}>

          {/* Glow blobs */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-1/3 h-[500px] w-[500px] rounded-full opacity-20 blur-3xl"
              style={{ background: "radial-gradient(circle, #7b00d4, transparent)" }} />
            <div className="absolute bottom-10 right-1/4 h-80 w-80 rounded-full opacity-15 blur-3xl"
              style={{ background: "radial-gradient(circle, #ec008c, transparent)" }} />
            <div className="absolute top-1/2 right-10 h-64 w-64 rounded-full opacity-10 blur-3xl"
              style={{ background: "radial-gradient(circle, #fc6767, transparent)" }} />
          </div>

          {/* Badge */}
          <div className="relative mb-6 inline-flex items-center gap-2 rounded-full border border-purple-500/30 bg-purple-500/10 px-4 py-1.5 text-xs font-semibold text-purple-300 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-purple-400 animate-pulse" />
            Free AI Nude Generator · No Watermarks · 100% Private
          </div>

          {/* Headline */}
          <h1 className="relative mb-4 max-w-4xl text-5xl md:text-7xl font-black leading-tight tracking-tight">
            The Free{" "}
            <span className="!bg-clip-text text-transparent"
              style={{ background: "linear-gradient(135deg,#c084fc,#ec008c,#fc6767)" }}>
              AI Nude Generator
            </span>
            <br />
            Built for Explicitness
          </h1>

          <p className="relative mb-8 max-w-xl text-base md:text-lg text-foreground/60 leading-relaxed">
            Describe any nude image — realistic, anime, fantasy, 3D — and the AI renders it in under 10 seconds.
            No censorship. No watermarks. No sign-up required to browse.
          </p>

          <div className="relative flex flex-col sm:flex-row items-center gap-4 mb-12">
            <CtaButton text="Generate AI Nudes Free →" large />
            <span className="text-xs text-foreground/40">No credit card · Instant access · Private by default</span>
          </div>

          {/* Stats */}
          <div className="relative flex flex-wrap justify-center gap-8 mb-10">
            {[
              { v: "2M+",    l: "Nudes Generated" },
              { v: "< 10s",  l: "Per Generation" },
              { v: "6+",     l: "Art Styles" },
              { v: "0",      l: "Watermarks" },
              { v: "100%",   l: "Private" },
            ].map(({ v, l }) => (
              <div key={l} className="text-center">
                <div className="text-2xl font-black !bg-clip-text text-transparent"
                  style={{ background: "linear-gradient(135deg,#c084fc,#ec008c)" }}>{v}</div>
                <div className="text-xs text-foreground/40 mt-0.5">{l}</div>
              </div>
            ))}
          </div>

          {/* Floating image strip */}
          <div className="relative flex gap-3 overflow-hidden max-w-2xl">
            {[hero1, hero2, hero3, hero4, hero5].map((img, i) => (
              <Link key={i} to="/create" title="Generate AI nude image"
                className="flex-shrink-0 overflow-hidden rounded-xl border-2 border-white/10 transition hover:border-purple-400 hover:scale-105"
                style={{ width: 90, height: 120 }}>
                <img src={img} alt="AI nude example"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: "center 30%", transform: "scale(1.4)", transformOrigin: "center 35%" }} />
              </Link>
            ))}
          </div>
          <p className="relative mt-3 text-xs text-foreground/30">
            Examples of AI nude art generated on this platform
          </p>
        </section>

        {/* ── FEATURES — alternating left/right ── */}
        {features.map((f, idx) => {
          const imgLeft = idx % 2 === 0;
          return (
            <section key={f.badge}
              className={`py-16 px-4 ${idx % 2 === 0 ? "bg-black/30" : "bg-black/50"}`}>
              <div className={`mx-auto max-w-5xl flex flex-col gap-10 md:flex-row md:items-center ${imgLeft ? "" : "md:flex-row-reverse"}`}>

                {/* Image */}
                <div className="w-full md:w-2/5 flex-shrink-0">
                  <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-purple-900/20">
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
                    <img src={f.image} alt={f.alt}
                      className="w-full object-cover"
                      style={{ aspectRatio: "4/3", objectFit: "cover", objectPosition: "center 30%",
                        transform: "scale(1.4)", transformOrigin: "center 35%" }} />
                    <div className="absolute bottom-4 left-4 z-20">
                      <span className="rounded-full px-3 py-1 text-xs font-black text-white shadow"
                        style={{ background: "linear-gradient(135deg,#7b00d4,#ec008c)" }}>
                        {f.badge}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h2 className="mb-4 text-2xl md:text-3xl font-black leading-snug">{f.heading}</h2>
                  <p className="text-foreground/70 text-base leading-relaxed mb-6">{f.body}</p>
                  <CtaButton text="Try It Free →" />
                </div>
              </div>
            </section>
          );
        })}

        {/* ── HOW TO USE ── */}
        <section className="py-16 px-4 bg-black/40">
          <div className="mx-auto max-w-5xl">
            <div className="mb-10 text-center">
              <h2 className="text-3xl md:text-4xl font-black mb-2">How to Use the AI Nude Generator</h2>
              <p className="text-foreground/50 text-sm">5 steps. Under 2 minutes start to finish.</p>
            </div>

            <div className="flex flex-col md:flex-row gap-10 items-center">
              {/* Image */}
              <div className="w-full md:w-2/5 flex-shrink-0">
                <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-purple-900/20">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent z-10" />
                  <img src={hero5} alt="How to use AI nude generator"
                    className="w-full object-cover"
                    style={{ aspectRatio: "4/3", objectFit: "cover", objectPosition: "center 25%",
                      transform: "scale(1.4)", transformOrigin: "center 30%" }} />
                </div>
              </div>

              {/* Steps */}
              <div className="flex-1 flex flex-col gap-4">
                {steps.map(({ n, title, desc }) => (
                  <div key={n} className="flex gap-4 items-start rounded-xl border border-white/8 bg-white/3 p-4 hover:bg-white/6 transition">
                    <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full text-sm font-black text-white"
                      style={{ background: "linear-gradient(135deg,#7b00d4,#ec008c)" }}>
                      {n}
                    </div>
                    <div>
                      <h3 className="font-bold text-sm mb-1">{title}</h3>
                      <p className="text-xs text-foreground/60 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
                <div className="mt-2">
                  <CtaButton text="Start Generating →" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── STYLES ── */}
        <section className="py-16 px-4 bg-black/25">
          <div className="mx-auto max-w-5xl">
            <div className="mb-10 text-center">
              <h2 className="text-3xl md:text-4xl font-black mb-2">AI Nude Art Styles</h2>
              <p className="text-foreground/50 text-sm max-w-lg mx-auto">
                Each style runs a different model — not just a filter. Switch between them freely on every generation.
              </p>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3">
              {styles.map(({ emoji, title, desc }) => (
                <div key={title}
                  className="group rounded-2xl border border-white/8 bg-white/3 p-6 hover:border-purple-500/40 hover:bg-white/6 transition cursor-pointer">
                  <div className="mb-3 text-4xl">{emoji}</div>
                  <h3 className="mb-2 font-bold text-base">{title}</h3>
                  <p className="text-sm text-foreground/60 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-center">
              <CtaButton text="Generate Your Style →" />
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-16 px-4 bg-black/40">
          <div className="mx-auto max-w-4xl">
            <div className="mb-10 text-center">
              <h2 className="text-3xl md:text-4xl font-black mb-2">Frequently Asked Questions</h2>
              <p className="text-foreground/50 text-sm">Everything about the AI Nude Generator.</p>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {/* Left column */}
              <div className="rounded-2xl border border-white/8 bg-white/3 px-5 overflow-hidden">
                {faqs.slice(0, 5).map(({ q, a }) => <FaqItem key={q} q={q} a={a} />)}
              </div>
              {/* Right column */}
              <div className="rounded-2xl border border-white/8 bg-white/3 px-5 overflow-hidden">
                {faqs.slice(5).map(({ q, a }) => <FaqItem key={q} q={q} a={a} />)}
              </div>
            </div>
          </div>
        </section>

        {/* ── RELATED TOOLS ── */}
        <section className="py-12 px-4 bg-black/40">
          <div className="mx-auto max-w-4xl">
            <h2 className="mb-6 text-center text-xl font-black text-foreground/80">Explore More AI Generators</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Link
                to="/create/ai-boobs"
                className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-5 transition hover:border-pink-500/40 hover:bg-white/10"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-pink-600 to-rose-800 text-2xl">🍒</div>
                <div>
                  <div className="font-bold group-hover:text-pink-400 transition">AI Boobs Generator</div>
                  <div className="text-xs text-foreground/50">Generate any bust style — realistic, anime, 3D</div>
                </div>
                <span className="ml-auto text-foreground/30 group-hover:text-pink-400 transition">→</span>
              </Link>
              <Link
                to="/create/ai-blowjob-generator"
                className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-5 transition hover:border-rose-500/40 hover:bg-white/10"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-rose-600 to-pink-800 text-2xl">💋</div>
                <div>
                  <div className="font-bold group-hover:text-rose-400 transition">AI Blowjob Generator</div>
                  <div className="text-xs text-foreground/50">Realistic & anime blowjob scenes generated by AI</div>
                </div>
                <span className="ml-auto text-foreground/30 group-hover:text-rose-400 transition">→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* ── FINAL CTA ── */}
        <section className="relative py-24 px-4 text-center overflow-hidden"
          style={{ background: "radial-gradient(ellipse at center, #1a0030 0%, #0a000f 80%)" }}>
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full opacity-25 blur-3xl"
              style={{ background: "radial-gradient(circle, #7b00d4, transparent)" }} />
          </div>
          <div className="relative mx-auto max-w-2xl">
            <h2 className="mb-3 text-3xl md:text-4xl font-black">
              Generate the nude image<br />you actually want.
            </h2>
            <p className="mb-8 text-foreground/50 text-base max-w-md mx-auto">
              Realistic, anime, 3D, fantasy — any body type, any style.
              Free credits included. No card, no watermark, no compromise.
            </p>
            <CtaButton text="Generate AI Nudes Free →" large />
          </div>
        </section>

      </main>
    </>
  );
};

export default AiNudePage;
